<template>
    <div class="biometrics-import mb-3 mt-page">

        <!-- action buttons -->
        <div class="float-right">
            <b-btn variant="primary" class="mr-2"
                   @click="reset()">
                {{ $i18n.gt('LABEL_RESET') }}
            </b-btn>
            <b-btn variant="primary"
                   :disabled="!dataRows"
                   @click="upload()">
                {{ $i18n.gt('LABEL_IMPORT') }}
            </b-btn>
        </div>

        <h1>{{ $i18n.gt('HEADING_IMPORT_BIOMETRICS') }}</h1>

        <b-row class="justify-content-center">
            <b-col cols="6" col-offset="3">
                <b-form-file
                        v-model="file"
                        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                        :state="Boolean(file)"
                        placeholder="Select file..."
                        drop-placeholder="Drop file here...">
                </b-form-file>

                <div class="my-3"
                     v-if="file">
                    <b-form-checkbox v-model="hasHeaderRow">
                        {{ $i18n.gt('LABEL_BIOMETRICS_IMPORT_FILE_HAS_HEADER_ROW') }}
                    </b-form-checkbox>
                </div>

                <div class="my-3"
                     v-if="file">
                    <label>{{ $i18n.gt('LABEL_BIOMETRICS_IMPORT_ENTER_COMMENT') }}:</label>
                    <b-form-input type="text" v-model="comment">
                    </b-form-input>
                </div>
            </b-col>
        </b-row>

        <b-row class="justify-content-center">
            <b-col cols="6" col-offset="3">
                <b-alert show variant="info" class="mt-3">
                    <div v-if="!dataRows">
                        <p>
                            {{ $i18n.gt('LABEL_BIOMETRICS_IMPORT_SELECT_CSV') }}
                        </p>
                        <p>
                            {{ $i18n.gt('LABEL_BIOMETRICS_IMPORT_CSV_MUST_MATCH_FORMAT') }}
                            <br/>
                            <b-btn variant="primary" size="sm" class="mt-2"
                                   href="/csv-templates/Wellcoaches biometrics import template.csv">
                                <shs-icon name="data-transfer-download"></shs-icon>
                                {{ $i18n.gt('LABEL_BIOMETRICS_IMPORT_DOWNLOAD_CSV_TEMPLATE') }}
                            </b-btn>
                        </p>
                    </div>
                    <p v-else>
                        {{ $i18n.gt('LABEL_BIOMETRICS_IMPORT_REVIEW_SAMPLE_DATA') }}
                    </p>
                </b-alert>
            </b-col>
        </b-row>
        <b-row class="justify-content-center">
            <b-col cols="6" col-offset="3">
                <b-alert :show="hasInvalidData" variant="danger">
                    {{ $i18n.gt('LABEL_BIOMETRICS_IMPORT_INVALID_DATA_FOUND') }}
                </b-alert>
            </b-col>
        </b-row>

        <b-row class="justify-content-center">
            <b-col>
                <spinner-until :condition="!loading">
                    <table class="table table-striped my-3">
                        <thead>
                        <tr>
                            <th class="text-center"
                                :colspan="permittedColumns.length">
                                <h3>{{ $i18n.gt('HEADING_REQUIRED_COLUMNS') }}</h3>
                            </th>
                        </tr>
                        <tr>
                            <th v-for="(col, c) in permittedColumns" :key="c">
                                {{col[1]}}
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(row,r) in dataRows" :key="r">
                            <td v-for="(col, c) in permittedColumns" :key="c"
                                :class="getDataCellClass(r,c)">
                                {{ row[c] }}
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </spinner-until>
            </b-col>
        </b-row>

    </div>
</template>

<script>

    import Vue from 'vue'
    import Papa from 'papaparse'
    import moment from 'moment'
    import * as _ from 'lodash'

    /**
     * Page to import Participant biometric responses from a CSV file
     */
    export default Vue.extend({
        name: 'BiometricsImport',
        components: {},
        data () {
            return {
                file: null,
                hasHeaderRow: true,
                loading: false,
                comment: 'Batch import ' + moment().format('M/D/YY'),
                dataRows: null,
                permittedColumns: [
                    ['external_id', 'Insurance Id'],
                    ['tchol', 'TCHOL'],
                    ['ldl_c', 'LDL'],
                    ['hdl_c', 'HDL'],
                    ['trig', 'Trig'],
                    ['fasting_bg', 'Fasting BG'],
                    ['a1c', 'A1C'],
                ],
            }
        },
        computed: {
            hasInvalidData () {
                let has = false
                _.each(this.dataRows, (row, r) => {
                    _.each(row, (col, c) => {
                        if (this.getDataCellClass(r, c) == 'error-cell') {
                            has = true
                        }
                    })
                })
                return has
            }
        },
        methods: {
            parseFile () {
                this.loading = true
                let reader = new FileReader()
                reader.readAsText(this.file, 'UTF-8')
                reader.onload = (evt) => {
                    Papa.parse(evt.target.result, {
                        worker: true,
                        complete: (results, file) => {
                            this.loading = false
                            this.dataRows = results.data
                        },
                        error: () => {
                            this.loading = false
                            alert('Error loading CSV file')
                        }
                    })
                }
                reader.onerror = function () {
                    this.loading = false
                    alert('Error loading CSV file')
                }
            },
            getDataCellClass (row, col) {
                if (this.hasHeaderRow && row == 0) {
                    // header row is only valid if hasHeaderRow is checked
                    return 'header-cell'
                } else if (col == 0) {
                    // external ID can be anything
                    return ''
                } else if (_.isNaN(_.toNumber(this.dataRows[row][col]))) {
                    // every other column must be numeric
                    return 'error-cell'
                } else {
                    return ''
                }
            },
            reset () {
                this.dataRows = null
                this.file = null
            },
            upload () {
                this.$api.importBiometrics(this, this.dataRows, this.hasHeaderRow, this.comment)
                    .then((response) => {
                        this.$toasted.show('Biometrics imported', { duration: 3000 })
                    })
                    .catch((e) => {
                        this.$utils.handleError(e, 'Error importing biometrics')
                    })
            }
        },
        watch: {
            // watch for file selection
            file (newVal, oldVal) {
                if (newVal) {
                    this.parseFile()
                }
            }
        }
    })

</script>

<style lang="scss">
    .biometrics-import {
        .header-cell {
            color: #ccc;
        }

        .error-cell {
            color: red;
        }

        .sample-table {
            width: 100%;
            overflow-x: scroll;
        }
    }
</style>
